import { useStaffChatPageHints } from "context/StaffChatPageHintsContext";
import {
  collectRegisteredFieldRules,
  formErrorsSignature,
  getCreateEstimateBlockers,
} from "pages/CreateOrder/utils/createEstimateBlockers";
import { buildEstimateFormSnapshot } from "pages/CreateOrder/utils/estimateFormSnapshot";
import { useCallback, useEffect } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "store";

export const BLOCKERS_DEBOUNCE_MS = 300;
/** While the form is invalid, how often the rules are read again (see the interval below). */
export const BLOCKERS_RECHECK_MS = 1000;

/**
 * CLE-3043 — publishes why "Create and Send" is disabled to the staff chat widget
 * (StaffChatPageHintsContext → `data.page.blockingIssues`). Recomputed from the live form, at most
 * once per BLOCKERS_DEBOUNCE_MS after a value or a field error changes; cleared when the page
 * unmounts so no other page inherits it. Labels only — see getCreateEstimateBlockers.
 *
 * CLE-3043 follow-up — on the same schedule it also publishes what has been typed so far
 * (`data.page.form`, buildEstimateFormSnapshot: the patient, payer, date of service and procedures,
 * never phone, email or benefits), so "this patient" on the New Estimate page means the form. Also
 * cleared on unmount. On the review / copy pages the API reads it beside the saved estimate, so
 * an edit not saved yet is what "this patient's payer" answers from.
 *
 * A Controller re-registers its rules on every render, so a rule can change (Not scheduled, an
 * outreach or payment-plan switch) with no value or error changing — nothing above would re-run.
 * So while the form is invalid the rules are also read again every BLOCKERS_RECHECK_MS; an equal
 * list is not re-published (StaffChatPageHintsProvider keeps the old reference).
 */
export function usePublishCreateEstimateBlockers(): void {
  const { control, getValues } = useFormContext();
  const values = useWatch({ control });
  // Reading `errors` subscribes to them. RHF mutates the errors object in place, so the effect keys
  // on a signature of it (field path + rule type), not on its reference.
  const { isValid, errors } = useFormState({ control });
  const errorsKey = formErrorsSignature(errors);
  const loadingPriceBreakdown = useSelector(
    (state: RootState) => state.serviceDetailsSlice.loadingPriceBreakdown
  );
  const { setBlockingIssues, setForm } = useStaffChatPageHints();

  const publish = useCallback(() => {
    const formValues = getValues();
    setBlockingIssues(
      getCreateEstimateBlockers(formValues, {
        // The rules react-hook-form validates with: every Controller re-registers its rules on render.
        // eslint-disable-next-line no-underscore-dangle
        fields: collectRegisteredFieldRules(control._fields),
        isValid,
        isPriceLoading: !!loadingPriceBreakdown,
      })
    );
    setForm(buildEstimateFormSnapshot(formValues));
  }, [
    control,
    getValues,
    isValid,
    loadingPriceBreakdown,
    setBlockingIssues,
    setForm,
  ]);

  useEffect(() => {
    const timer = setTimeout(publish, BLOCKERS_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [values, errorsKey, publish]);

  useEffect(() => {
    if (isValid) return undefined;
    const interval = setInterval(publish, BLOCKERS_RECHECK_MS);
    return () => clearInterval(interval);
  }, [isValid, publish]);

  useEffect(
    () => () => {
      setBlockingIssues(null);
      setForm(null);
    },
    [setBlockingIssues, setForm]
  );
}
